/**
 * NAV-6.2 — layout-matched first paint for non-Commission /app destinations.
 *
 * Reuses `@/components/ui/skeleton`. Kinds match the loaded chrome of each
 * page: dashboard selector + tiles, chat shell, earnings tiles + list,
 * kanban columns, settings groups + card, table rows, calendar grid, form
 * cards. Do not invent Overview bands or lock cards here.
 */
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import type { AppOutletSkeletonKind } from '@/lib/outletSkeleton';

/** Mirrors Sidebar `settingsGroups` captions — labels only, no hrefs. */
const SETTINGS_SKELETON_GROUPS: readonly { label: string; items: number }[] = [
  { label: 'People & Structure', items: 3 },
  { label: 'Workflow', items: 5 },
  { label: 'Connections', items: 2 },
  { label: 'System', items: 4 },
];

function DashboardSkeleton() {
  return (
    <div className="container mx-auto px-6 py-3" aria-hidden>
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-5 w-5 rounded-full" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-10 w-28" />
          <Skeleton className="h-10 w-16" />
        </div>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 6 }, (_, i) => (
          <Skeleton key={i} className="h-40 rounded-lg" />
        ))}
      </div>
    </div>
  );
}

function ChatSkeleton() {
  return (
    <div className="relative flex h-full flex-col overflow-hidden" aria-hidden>
      <div className="flex items-center justify-between border-b p-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-8 w-24" />
      </div>
      <div className="flex min-h-0 flex-1">
        <div className="w-64 shrink-0 space-y-3 border-r p-4">
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-8 w-8 rounded-full" />
              <div className="min-w-0 flex-1 space-y-2">
                <Skeleton className="h-3 w-28" />
                <Skeleton className="h-3 w-20" />
              </div>
            </div>
          ))}
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex-1 space-y-4 p-4">
            {Array.from({ length: 5 }, (_, i) => (
              <div key={i} className="flex gap-3">
                <Skeleton className="h-8 w-8 shrink-0 rounded-full" />
                <div className="min-w-0 flex-1 space-y-2">
                  <Skeleton className="h-3 w-32" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
              </div>
            ))}
          </div>
          <div className="border-t p-4">
            <Skeleton className="h-10 w-full" />
          </div>
        </div>
      </div>
    </div>
  );
}

function EarningsSkeleton() {
  return (
    <div className="space-y-3.5 p-6" aria-hidden>
      <div className="mb-6 flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton className="h-7 w-40" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Skeleton className="h-9 w-24" />
      </div>
      <div className="grid gap-3.5 sm:grid-cols-3">
        {Array.from({ length: 3 }, (_, i) => (
          <div
            key={i}
            className="relative overflow-hidden rounded-[15px] border bg-card px-5 pb-[15px] pt-[17px]"
          >
            <Skeleton className="h-3 w-20" />
            <Skeleton className="mt-2.5 h-8 w-28" />
            <Skeleton className="mt-2 h-3 w-36" />
          </div>
        ))}
      </div>
      <div className="overflow-hidden rounded-[15px] border bg-card">
        <div className="grid grid-cols-[minmax(0,2.4fr)_repeat(3,minmax(0,1fr))] gap-4 border-b bg-muted/40 px-5 py-3">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="ml-auto h-3 w-16" />
          <Skeleton className="ml-auto h-3 w-16" />
          <Skeleton className="ml-auto h-3 w-20" />
        </div>
        {Array.from({ length: 5 }, (_, i) => (
          <div
            key={i}
            className="grid grid-cols-[minmax(0,2.4fr)_repeat(3,minmax(0,1fr))] gap-4 px-5 py-4"
          >
            <Skeleton className="h-4 w-40" />
            <Skeleton className="ml-auto h-4 w-16" />
            <Skeleton className="ml-auto h-4 w-16" />
            <Skeleton className="ml-auto h-4 w-16" />
          </div>
        ))}
      </div>
    </div>
  );
}

function KanbanSkeleton() {
  return (
    <div className="flex h-full flex-col overflow-hidden" aria-hidden>
      <div className="px-4 py-3">
        <Skeleton className="h-8 w-48" />
      </div>
      <div className="flex w-full flex-1 gap-4 overflow-x-auto px-4">
        {Array.from({ length: 4 }, (_, i) => (
          <div
            key={i}
            className="h-full w-[300px] min-w-[300px] max-w-[300px] flex-shrink-0"
          >
            <div className="rounded-t-lg border-b border-border bg-muted px-3 py-2">
              <Skeleton className="mx-auto h-5 w-24" />
            </div>
            <div className="flex flex-col gap-2 p-2">
              {Array.from({ length: 3 }, (_, cardIdx) => (
                <Skeleton
                  key={cardIdx}
                  className="h-24 w-full rounded-lg border border-border"
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SettingsSkeleton() {
  return (
    <div className="flex min-h-[calc(100vh-4rem)]" aria-hidden>
      <nav className="w-56 shrink-0 border-r border-border bg-muted/30 px-3 py-6">
        {SETTINGS_SKELETON_GROUPS.map((group) => (
          <div key={group.label} className="mb-4 space-y-2">
            <Skeleton className="h-3 w-28" />
            {Array.from({ length: group.items }, (_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        ))}
      </nav>
      <div className="min-w-0 flex-1 space-y-4 p-6">
        <Skeleton className="h-8 w-48" />
        <Card>
          <CardHeader className="space-y-2">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-4 w-full max-w-lg" />
          </CardHeader>
          <CardContent className="space-y-3">
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="container mx-auto space-y-4 p-6" aria-hidden>
      <Skeleton className="h-8 w-48" />
      <div className="divide-y rounded-md border">
        <div className="flex items-center justify-between gap-3 bg-muted/40 p-3">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-20" />
        </div>
        {Array.from({ length: 8 }, (_, i) => (
          <div key={i} className="flex items-center justify-between gap-3 p-3">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-4 w-16" />
          </div>
        ))}
      </div>
    </div>
  );
}

function CalendarSkeleton() {
  return (
    <div className="container mx-auto max-w-7xl p-0" aria-hidden>
      <div className="mb-6 flex items-center justify-between">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-10 w-72" />
      </div>
      <div className="grid grid-cols-7 gap-2">
        {Array.from({ length: 35 }, (_, i) => (
          <Skeleton key={i} className="h-20 rounded-md" />
        ))}
      </div>
    </div>
  );
}

function FormSkeleton() {
  return (
    <div className="container mx-auto max-w-5xl space-y-4 p-4 md:p-8" aria-hidden>
      <Skeleton className="h-8 w-40" />
      {Array.from({ length: 2 }, (_, card) => (
        <Card key={card}>
          <CardHeader className="space-y-2">
            <Skeleton className="h-5 w-36" />
            <Skeleton className="h-4 w-full max-w-md" />
          </CardHeader>
          <CardContent className="space-y-4">
            {Array.from({ length: 4 }, (_, i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-10 w-full" />
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export function OutletDestinationSkeleton({
  kind,
}: {
  kind: AppOutletSkeletonKind;
}) {
  switch (kind) {
    case 'dashboard':
      return <DashboardSkeleton />;
    case 'chat':
      return <ChatSkeleton />;
    case 'earnings':
      return <EarningsSkeleton />;
    case 'kanban':
      return <KanbanSkeleton />;
    case 'settings':
      return <SettingsSkeleton />;
    case 'table':
      return <TableSkeleton />;
    case 'calendar':
      return <CalendarSkeleton />;
    case 'form':
      return <FormSkeleton />;
  }
}
